import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { environment } from '../../../environments/environment.dev';
import { AuthenticationService } from "../../@core/services/authentication.service";
import { BaseService } from "../../@core/services/base.service";
import { DEACTIVATION_STATUS, IDocumentMetrics, IMilestone, IProject, IShareUser, IStatus } from "../../@core/models/model";

export interface IProjectFilter {
    searchValue?: string;
    customerName?: string;
    managerIds?: string[];
    status?: string[];
    dueDate?: {
        from?: string;
        to?: string;
    },
    createdDate?: {
        from?: string;
        to?: string;
    },
    sortField?: string;
    sortOrder?: string;
    ids?: string[];
    active?: boolean;
    workspaceFilter?: boolean;
}

export interface IPeopleTrackerData {
    user: {
        _id: string,
        fullName: string,
        deactivationStatus: DEACTIVATION_STATUS
    },
    questions: {
        answered: number;
        unanswered: number;
        approved: number;
        rejected: number;
        total: number;
        progress: number;
    };
    documents: {
        pending: number;
        approved: number;
        total: number;
        progress: number;
    };
    totalProgress: number;
    lastNotifiedAt: string | null;
}
@Injectable({ providedIn: 'root' })
export class ProjectService extends BaseService {
    projectsUpdated = new Subject<void>();

    constructor(
        protected override http: HttpClient,
        private authService: AuthenticationService
    ) {
        super(http);
    }

    projectUrl = environment.apiUrl + '/project'

    getProjects(skip: number = 0, limit: number = 10, filters: IProjectFilter = {}) {
        return this.post<{
            projects: IProject[],
            totalRecords: number
        }>(this.projectUrl + '/all', {
            skip,
            limit,
            filters
        })
    }

    getQuestionAssignees(projectId: string) {
        return this.get<IPeopleTrackerData[]>(this.projectUrl + '/question-assignees/' + projectId)
    }

    saveProject(data: {
        project: Partial<IProject>,
        milestones: IMilestone[],
        deletedMilestones?: string[],
        teamMembers: IShareUser[],
        workspaceShare?: boolean,
        projectShare?: boolean,
        projectKnowledge?: { files: File[], knowledgeBases: string[] }
    }) {
        // Ensure managers is an array
        if (data.project.managers && !Array.isArray(data.project.managers)) {
            data.project.managers = [data.project.managers];
        }

        // Always use FormData - it can handle both files and JSON data
        const formData = new FormData();

        // Append project data as JSON
        formData.append('project', JSON.stringify(data.project));

        // Append milestones as JSON
        formData.append('milestones', JSON.stringify(data.milestones));

        // Append deleted milestones if any
        if (data.deletedMilestones && data.deletedMilestones.length > 0) {
            formData.append('deletedMilestones', JSON.stringify(data.deletedMilestones));
        }

        // Append team members as JSON
        formData.append('teamMembers', JSON.stringify(data.teamMembers));

        // Append workspace and project share flags
        if (data.workspaceShare !== undefined) {
            formData.append('workspaceShare', data.workspaceShare.toString());
        }
        if (data.projectShare !== undefined) {
            formData.append('projectShare', data.projectShare.toString());
        }

        // Append project knowledge files if any
        if (data.projectKnowledge && data.projectKnowledge.files && data.projectKnowledge.files.length > 0) {
            data.projectKnowledge.files.forEach((file) => {
                formData.append('projectKnowledgeFiles', file);
            });
        }

        // Append knowledge bases as JSON if any
        if (data.projectKnowledge && data.projectKnowledge.knowledgeBases && data.projectKnowledge.knowledgeBases.length > 0) {
            formData.append('projectKnowledgeBases', JSON.stringify(data.projectKnowledge.knowledgeBases));
        }

        return this.post<IProject>(this.projectUrl + '/create', formData);
    }

    pinDocumentsToProject(projectId: string, documents: string[]) {
        return this.post<{ success: boolean }>(this.projectUrl + '/pin-document', {
            projectId,
            documents
        });
    }

    unpinDocumentsFromProject(projectId: string, documents: string[]) {
        return this.post<{ success: boolean }>(this.projectUrl + '/unpin-document', {
            projectId,
            documents
        });
    }

    updateProject(data: Partial<IProject>, projectId: string) {
        return this.post<IProject>(this.projectUrl + '/update/' + projectId, data)
    }

    deleteProject(projectId: string, isActive: boolean) {
        return this.delete<IProject>(this.projectUrl + '/delete/' + projectId, {
            isActive
        });
    }

    sendReminderToAssignees(projectId: string, assigneeIds: string[] = [], message: string) {
        return this.post<{ success: boolean }>(this.projectUrl + '/assignee-reminders', {
            projectId,
            assigneeIds,
            message
        })
    }

    updateProjectTeam(projectId: string, teamMembers: IShareUser[], workspaceShare: boolean, projectShare: boolean) {
        return this.post<IProject>(this.projectUrl + '/update-team-members', {
            projectId,
            teamMembers,
            workspaceShare,
            projectShare
        })
    }

    getDocumentMetrics(projectId: string) {
        return this.get<IDocumentMetrics>(this.projectUrl + '/document-metrics', { projectId })
    }

    // Get project tag types
    getProjectTagTypes() {
        return this.get<Array<{ _id: string; name: string; dataType: string }>>(this.projectUrl + '/tag-types');
    }

    // Download project documents and knowledge as ZIP
    downloadProjectDocumentsAndKnowledge(projectId: string) {
        return this.http.get(this.projectUrl + '/download/' + projectId, {
            responseType: 'blob',
            observe: 'response'
        });
    }
}
